/**
 * Bets by Sport API client — fetches betting activity data from Snowflake via the backend.
 */
import type { ApiClient } from '../api/client.ts';

// ─── Types ───────────────────────────────────────────────────────────────────

export type Period = 'today' | '24h' | '7d' | '30d' | '90d' | 'custom';

export interface BetsBySportKpis {
  readonly totalHandle: number;
  readonly totalGGR: number;
  readonly totalBets: number;
  readonly totalPlayers: number;
  readonly avgBet: number;
  readonly holdPercent: number;
}

export interface SportRow {
  readonly SPORT: string;
  readonly BET_COUNT: number;
  readonly TOTAL_HANDLE: number;
  readonly TOTAL_GGR: number;
  readonly TOTAL_PAYOUT: number;
  readonly AVG_BET: number;
  readonly UNIQUE_PLAYERS: number;
}

export interface ChannelRow {
  readonly CHANNEL_ID: string;
  readonly BET_COUNT: number;
  readonly TOTAL_HANDLE: number;
  readonly TOTAL_GGR: number;
  readonly UNIQUE_PLAYERS: number;
}

export interface DailyTrendRow {
  readonly DATE: string;
  readonly BET_COUNT: number;
  readonly TOTAL_HANDLE: number;
  readonly TOTAL_GGR: number;
  readonly UNIQUE_PLAYERS: number;
}

export interface LeagueRow {
  readonly SPORT: string;
  readonly LEAGUE: string;
  readonly BET_COUNT: number;
  readonly TOTAL_HANDLE: number;
  readonly TOTAL_GGR: number;
}

export interface StatusRow {
  readonly STATUS: string;
  readonly BET_COUNT: number;
  readonly TOTAL_HANDLE: number;
  readonly TOTAL_GGR: number;
}

export interface BetsBySportResponse {
  readonly period: Period;
  readonly fromDate: string;
  readonly toDate: string;
  readonly kpis: BetsBySportKpis;
  readonly bySport: readonly SportRow[];
  readonly byChannel: readonly ChannelRow[];
  readonly dailyTrend: readonly DailyTrendRow[];
  readonly topLeagues: readonly LeagueRow[];
  readonly byStatus: readonly StatusRow[];
}

// ─── API ─────────────────────────────────────────────────────────────────────

const passthrough = { safeParse: (v: unknown) => ({ success: true as const, data: v }) } as never;

export interface BetsBySportApi {
  readonly getData: (period: Period, from?: string, to?: string) => Promise<BetsBySportResponse>;
}

export const createBetsBySportApi = (client: ApiClient): BetsBySportApi => ({
  getData: async (period, from, to) => {
    const params = new URLSearchParams({ period });
    if (from) params.set('from', from);
    if (to) params.set('to', to);

    const result = await client.get(
      `/api/admin/analytics/bets-by-sport?${params.toString()}`,
      passthrough,
    );
    if (!result.ok) throw new Error(result.failure.message);
    return result.data as unknown as BetsBySportResponse;
  },
});
